import { ApiError } from '~/api/errors';
import { getLocalTimeZone, localDateTimeToISO } from '~/utils/date';

import {
  NEW_RUN_POST_STEPS,
  parseProductIds,
  type NewRunFilterRow,
  type NewRunFormValues,
} from '../types/new-run.types';
import type {
  CatalogOperationsTrigger,
  ConnectorOption,
  TriggerFilterRead,
  UseCaseConfig,
} from '../types/runs.api.types';
import { RUN_USE_CASES } from '../types/runs.types';
import type { ScheduleUpsert } from '../types/schedules.api.types';

/** Selector value meaning "the selection goes in `useCase.fields`" (GMC); anything else is Meta's key list. */
const FIELDS_SELECTOR = 'useCase.fields';

/**
 * One filter row → its wire value by the predefined key's type; `undefined` drops an empty row.
 * Exported so the "Add filter" panel can preview B3's match count for a row before it's added.
 */
export function filterValue(row: NewRunFilterRow, def: TriggerFilterRead | undefined): unknown {
  const values = row.values.map((v) => v.trim()).filter(Boolean);
  if (values.length === 0) return undefined;
  if (row.kind === 'dynamic') {
    // A list matches any value (IN); a single string is a contains match.
    return values.length === 1 ? values[0] : values;
  }
  switch (def?.type) {
    case 'multi_select':
      return values;
    case 'boolean':
      return values[0] === 'true';
    case 'text':
    case 'text_contains':
      return values[0];
    default:
      return values.length === 1 ? values[0] : values;
  }
}

/**
 * Filter rows → the trigger's `filters` map; `undefined` when nothing is set so the
 * key is omitted. Exported so the "already curated" check panel can submit its
 * breakdown against the exact same scope the run would actually use.
 */
export function toFilters(
  rows: NewRunFilterRow[],
  defs: TriggerFilterRead[],
): Record<string, unknown> | undefined {
  const out: Record<string, unknown> = {};
  for (const row of rows) {
    const key = row.key.trim();
    // A duplicate key (the UI blocks adding one, but a copied draft can still carry one) keeps
    // its FIRST row rather than letting a later one silently overwrite it.
    if (!key || key in out) continue;
    const value = filterValue(
      row,
      defs.find((d) => d.key === key),
    );
    if (value !== undefined) out[key] = value;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/** Form values → the `useCase` block. Empty selections and untouched toggles are omitted. */
function toUseCase(values: NewRunFormValues, connector: ConnectorOption): UseCaseConfig {
  const type = RUN_USE_CASES.find((u) => u === values.useCase) ?? 'GMC_ADS';
  const useCase: UseCaseConfig = { type };

  // Omitted means "every field"; an empty list is refused (422), so never send [].
  const alwaysOn = new Set(
    (connector.enrichmentFields?.fields ?? []).filter((f) => f.alwaysOn).map((f) => f.code),
  );
  const picked = values.fields.filter((code) => !alwaysOn.has(code));
  if (picked.length > 0 && connector.enrichmentFields) {
    if (connector.enrichmentFields.selector === FIELDS_SELECTOR) useCase.fields = picked;
    else useCase.contentAttributeKeys = picked;
  }

  for (const step of NEW_RUN_POST_STEPS) {
    const flag = values.postSteps[step];
    if (flag !== undefined) useCase[step] = flag;
  }

  // Retailer prompts are GMC-only; blank means the shared prompt.
  const retailer = values.retailer.trim();
  if (type === 'GMC_ADS' && retailer) useCase.retailer = retailer;
  return useCase;
}

/**
 * Form values → `CatalogOperationsTrigger`. The server forbids unknown keys and refuses some empty
 * values, so every optional is added only when it carries a value; `waitForSignals` is sent at its
 * default (the flow's gates decide waiting). Batch size, sub-batch size and the cache override are
 * no longer offered in the K2 form — the service defaults apply. The Start section never touches
 * this body: a later start wraps it in a one-off schedule (`toOnceScheduleBody`).
 */
export function toTriggerBody(
  values: NewRunFormValues,
  connector: ConnectorOption,
  filterDefs: TriggerFilterRead[],
): CatalogOperationsTrigger {
  const body: CatalogOperationsTrigger = {
    connectorCode: values.connectorCode,
    useCase: toUseCase(values, connector),
    waitForSignals: true,
    recordSkipped: values.recordSkipped,
  };

  const name = values.name.trim();
  if (name) body.name = name;
  if (values.flow) body.flow = values.flow;
  if (values.ingestMode) body.ingestMode = values.ingestMode;
  if (values.maxProducts) body.maxProducts = Number(values.maxProducts);
  if (values.syncId) body.syncId = values.syncId.trim();

  const productIds = parseProductIds(values.productIds);
  if (productIds.length > 0) body.productIds = productIds;

  const filters = toFilters(values.filters, filterDefs);
  if (filters) body.filters = filters;

  return body;
}

/**
 * "Start later": the SAME trigger body an immediate start sends, frozen into a one-off schedule
 * (`once` + `startAt`, api-changes §74) on `POST …/schedules`. A schedule creates nothing until it
 * fires, so it never holds the connector's lane; `null` when the picked local time is unreadable.
 */
export function toOnceScheduleBody(
  trigger: CatalogOperationsTrigger,
  startAtLocal: string,
  name: string,
): ScheduleUpsert | null {
  const startAt = localDateTimeToISO(startAtLocal, getLocalTimeZone());
  if (!startAt) return null;
  return { name, once: true, startAt, trigger };
}

/**
 * True when a schedule create's 422 names `once` itself — an older backend that predates §74 and
 * forbids it as an unknown key — so the page can say one-off starts aren't available there.
 */
export function isOnceUnsupported(errors: NewRunServerError[]): boolean {
  return errors.some((err) => err.field === 'once');
}

/** A form field a 422 can be pinned to, or `null` for errors shown in the form banner. */
export type NewRunErrorField = keyof NewRunFormValues;

/** Server field path prefix (camelCase, `body.` stripped) → form field. Longest prefix wins. */
const SERVER_FIELD_MAP: [string, NewRunErrorField][] = [
  ['useCase.retailer', 'retailer'],
  ['useCase.fields', 'fields'],
  ['useCase.contentAttributeKeys', 'fields'],
  ['useCase.type', 'useCase'],
  ['useCase', 'useCase'],
  ['connectorCode', 'connectorCode'],
  ['name', 'name'],
  ['flow', 'flow'],
  ['syncId', 'syncId'],
  ['maxProducts', 'maxProducts'],
  ['ingestMode', 'ingestMode'],
  ['productIds', 'productIds'],
  ['filters', 'filters'],
  ['startAt', 'startAt'],
  ['recordSkipped', 'recordSkipped'],
];

/** One validation error from a 422 body (`details.errors[]`). */
export interface NewRunServerError {
  field: string;
  message: string;
  /** Form field it maps to; `null` when it can only be shown in the banner. */
  formField: NewRunErrorField | null;
}

/** Narrow unknown JSON to a plain object. */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Maps a server field path (`useCase.retailer`, `productIds.0`, …) onto a form field. */
function toFormField(field: string): NewRunErrorField | null {
  const match = SERVER_FIELD_MAP.find(
    ([prefix]) => field === prefix || field.startsWith(`${prefix}.`),
  );
  return match ? match[1] : null;
}

/**
 * Reads `details.errors[].{field,message}` from a catalog 422 (a trigger, or a one-off schedule
 * create) so the page can pin each one to its form field; anything unmapped gets `formField: null`.
 */
export function readTriggerValidationErrors(error: unknown): NewRunServerError[] {
  if (!(error instanceof ApiError) || error.statusCode !== 422 || !isRecord(error.body)) return [];
  const details = error.body.details;
  if (!isRecord(details) || !Array.isArray(details.errors)) return [];
  const out: NewRunServerError[] = [];
  for (const entry of details.errors) {
    if (!isRecord(entry)) continue;
    // A one-off schedule's body nests the trigger under `trigger.` — the same fields underneath.
    const field =
      typeof entry.field === 'string'
        ? entry.field.replace(/^body\.?/, '').replace(/^trigger\./, '')
        : '';
    const message = typeof entry.message === 'string' ? entry.message : '';
    if (!message) continue;
    out.push({ field, message, formField: toFormField(field) });
  }
  return out;
}

/** True when the error is a 409 (a run already holds this connector's lane). */
export function isLaneBusy(error: unknown): boolean {
  return error instanceof ApiError && error.statusCode === 409;
}

/** Narrow unknown JSON to a string, else `undefined`. */
function stringOf(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

/** Narrow unknown JSON to a finite number, else `undefined`. */
function numberOf(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/** Narrow unknown JSON to a boolean, else `undefined`. */
function boolOf(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined;
}

/** Narrow unknown JSON to a string array, dropping anything that isn't a string. */
function stringArrayOf(value: unknown): string[] | undefined {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : undefined;
}

/** A stored `filters` map (`Record<string, unknown>`) → form filter rows, classified by `defs`. */
export function filterRowsFromWire(filters: unknown, defs: TriggerFilterRead[]): NewRunFilterRow[] {
  if (!isRecord(filters)) return [];
  return Object.entries(filters).map(([key, raw]) => {
    const kind = defs.some((d) => d.key === key) ? 'predefined' : 'dynamic';
    const values = Array.isArray(raw)
      ? raw.map((v) => String(v))
      : typeof raw === 'boolean'
        ? [raw ? 'true' : 'false']
        : [String(raw)];
    return { kind, key, values };
  });
}

/** The `useCase` block of a stored request → the form's fields/postSteps/retailer/useCase type. */
function fromUseCaseWire(
  useCase: Record<string, unknown>,
): Pick<NewRunFormValues, 'fields' | 'postSteps'> & { useCase?: string; retailer?: string } {
  const fields = stringArrayOf(useCase.fields) ?? stringArrayOf(useCase.contentAttributeKeys) ?? [];
  const postSteps: NewRunFormValues['postSteps'] = {};
  for (const step of NEW_RUN_POST_STEPS) {
    const flag = boolOf(useCase[step]);
    if (flag !== undefined) postSteps[step] = flag;
  }
  const draft: Pick<NewRunFormValues, 'fields' | 'postSteps'> & {
    useCase?: string;
    retailer?: string;
  } = { fields, postSteps };
  const type = stringOf(useCase.type);
  if (type) draft.useCase = type;
  const retailer = stringOf(useCase.retailer);
  if (retailer) draft.retailer = retailer;
  return draft;
}

/**
 * "Copy settings from a batch": the stored `CatalogOperationsTrigger` a run was started with
 * (`RunRead.config.request`, already deployed — not hand-typed) → a partial New batch form draft.
 * Never includes `name`/`startMode`/`startAt`/`repeatMode` — a copy is a fresh batch, not a
 * reschedule of the old one. `null` when `request` isn't a usable object (an older/blank run).
 */
export function draftFromTriggerRequest(
  request: unknown,
  filterDefs: TriggerFilterRead[],
): Partial<NewRunFormValues> | null {
  if (!isRecord(request)) return null;
  const draft: Partial<NewRunFormValues> = {};

  const connectorCode = stringOf(request.connectorCode);
  if (connectorCode) draft.connectorCode = connectorCode;

  if (isRecord(request.useCase)) {
    const { useCase, fields, postSteps, retailer } = fromUseCaseWire(request.useCase);
    if (useCase) draft.useCase = useCase;
    draft.fields = fields;
    draft.postSteps = postSteps;
    if (retailer) draft.retailer = retailer;
  }

  const flow = stringOf(request.flow);
  if (flow) draft.flow = flow;
  const ingestMode = stringOf(request.ingestMode);
  if (ingestMode) draft.ingestMode = ingestMode;
  const maxProducts = numberOf(request.maxProducts);
  if (maxProducts !== undefined) draft.maxProducts = String(maxProducts);
  const syncId = stringOf(request.syncId);
  if (syncId) draft.syncId = syncId;
  const productIds = stringArrayOf(request.productIds);
  if (productIds && productIds.length > 0) draft.productIds = productIds.join('\n');
  const recordSkipped = boolOf(request.recordSkipped);
  if (recordSkipped !== undefined) draft.recordSkipped = recordSkipped;
  draft.filters = filterRowsFromWire(request.filters, filterDefs);

  return draft;
}

/**
 * "Copy settings from a batch", reconciling the copied filter rows against the TARGET connector's
 * own vocabulary — the same "not offered today, drop it" treatment `applyCopiedDraft` already
 * gives fields. A row whose key is one of the target's predefined keys is reclassified predefined
 * (its editor renders correctly even if it travelled as `dynamic` under the source connector); a
 * row that isn't predefined there is kept as a dynamic column only when the target's own column
 * list is known to contain it, or when that list is unknown (an older deploy) — never dropped on a
 * guess. Anything else (a predefined key the target doesn't have, and no matching dynamic column)
 * is dropped.
 */
export function reconcileFilterRows(
  rows: NewRunFilterRow[],
  defs: TriggerFilterRead[],
  dynamicColumns: string[] | null,
): NewRunFilterRow[] {
  const out: NewRunFilterRow[] = [];
  for (const row of rows) {
    if (defs.some((d) => d.key === row.key)) {
      out.push({ ...row, kind: 'predefined' });
    } else if (dynamicColumns === null || dynamicColumns.includes(row.key)) {
      out.push({ ...row, kind: 'dynamic' });
    }
  }
  return out;
}
